import { categories } from '@/mocks/categories';
import { posts } from '@/mocks/posts';
import type { Category, Post } from '@/types/content';

export function getCategoryBySlug(slug?: string): Category | undefined {
  if (!slug) return undefined;
  return categories.find((item) => item.slug === slug);
}

export function getPostBySlug(slug?: string): Post | undefined {
  if (!slug) return undefined;
  return posts.find((item) => item.slug === slug);
}

export function getPostsByCategory(slug: string): Post[] {
  return posts.filter((item) => item.categorySlug === slug);
}

export function countByCategory(slug: string): number {
  return getPostsByCategory(slug).length;
}

export function getFeaturedPosts(): Post[] {
  return posts.filter((item) => item.featured);
}

export function getHotPosts(limit = 6): Post[] {
  return [...posts].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatestPosts(limit?: number): Post[] {
  const sorted = [...posts].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
  return typeof limit === 'number' ? sorted.slice(0, limit) : sorted;
}

export function getRelatedPosts(post: Post, limit = 3): Post[] {
  const sameCategory = posts.filter(
    (item) => item.categorySlug === post.categorySlug && item.slug !== post.slug,
  );
  const others = posts.filter(
    (item) => item.categorySlug !== post.categorySlug && item.slug !== post.slug,
  );
  return [...sameCategory, ...others].slice(0, limit);
}

export function searchPosts(keyword: string): Post[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return posts.filter((item) => {
    const haystack = [item.title, item.excerpt, item.tags.join(' '), item.categorySlug]
      .join(' ')
      .toLowerCase();
    return haystack.includes(key);
  });
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

export function formatDate(date: string): string {
  const [y, m, d] = date.split('-');
  return `${y}年${Number(m)}月${Number(d)}日`;
}

export const toneClasses: Record<
  Category['tone'],
  { soft: string; solid: string; text: string; border: string }
> = {
  primary: {
    soft: 'bg-primary-100 text-primary-700',
    solid: 'bg-primary-500 text-background-50',
    text: 'text-primary-700',
    border: 'border-primary-200',
  },
  accent: {
    soft: 'bg-accent-100 text-accent-800',
    solid: 'bg-accent-500 text-foreground-950',
    text: 'text-accent-800',
    border: 'border-accent-200',
  },
  secondary: {
    soft: 'bg-secondary-100 text-secondary-800',
    solid: 'bg-secondary-500 text-background-50',
    text: 'text-secondary-800',
    border: 'border-secondary-200',
  },
};